import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IFilter, PageSize, IGridResult, IPagedResults } from 'src/app/core/models/base';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { AbsenceState, AbsenceType, IAbsenceGroup, ICreateAbsenceGroupApplicationRequest } from '../../models/absence-group';
import { map, Observable, tap } from 'rxjs';
import { IEmployee } from 'src/app/employee/employee';
import { AbsenceGroupStoreService } from '../../storage/absence-group-store.service';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { Store } from '@ngrx/store';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { ITenantSettings } from 'src/app/setting/tenant-settings/models/tenant-setting';
import { overtimeFlexTimeFallbackHours } from 'src/app/shared/helpers/overtime';

@UntilDestroy()
@Component({
    selector: 'app-my-absences',
    templateUrl: './my-absences.component.html',
    styleUrls: ['./my-absences.component.css'],
    providers: [AbsenceGroupStoreService],
    standalone: false
})
export class MyAbsencesComponent implements OnInit {

  constructor(
    private absenceGroupStore: AbsenceGroupStoreService,
    private store: Store<AppStateInterface>,
    private tenantSettingsStore: TenantSettingsStoreService
  ) { }

  // Overtime compensation only exists while the tenant runs overtime.
  absenceTypes$ = this.tenantSettingsStore.tenantSettings$.pipe(
    map(tenantSettings => enumToArray(AbsenceType, "absenceGroups.absenceTypes.")
      .filter(absenceType => tenantSettings?.hasOvertime || absenceType.id !== AbsenceType.OvertimeCompensation))
  );
  absenceStatesArray = enumToArray(AbsenceState, "absenceGroups.absenceStates.")

  tenantSettings$ = this.tenantSettingsStore.tenantSettings$;

  // Request form (self-service), shown in a popup.
  formGroup : UntypedFormGroup;

  // List filter (own absences only, so no employee selection).
  filterForm : UntypedFormGroup;

  // Whether the "request absence" popup is open.
  createOpen = false;

  absenceGroups: IPagedResults<IAbsenceGroup[]>;
  absenceGroupsGridData: IGridResult<IAbsenceGroup[]> = {
    data: [],
    total: 0
  };

  defaultAbsence = {
    id: null,
    name: 'Bitte wählen',
  }

  employee$ : Observable<IEmployee>;

  get absenceTypes(){
    return AbsenceType;
  }

  get pageSizes() {
    return PageSize;
  }


  ngOnInit(): void {
    // Ferien-Saldo ueber dem Grid; der Store wird von AppComponent/Login befuellt.
    this.employee$ = this.store.select(currentEmployeeSelector);

    this.store.select(currentEmployeeSelector)
    .pipe(
      untilDestroyed(this),
      tap(employee => {
        // Scope every request/reload to the logged-in employee.
        this.absenceGroupStore.searchAbseceGroupRequest.employeeId = employee.id;
      })
    ).subscribe(() => {
      this.absenceGroupStore.loadAbsenceGroups();
    });
    this.initForm();
    this.initFilterForm();
  }

  initForm(){
    this.formGroup = new UntypedFormGroup({
      title:  new UntypedFormControl(null),
      dateFrom:  new UntypedFormControl(new Date(), Validators.required),
      isHalfDayStart:  new UntypedFormControl(false),
      dateTo:  new UntypedFormControl(new Date(), Validators.required),
      isHalfDayEnd:  new UntypedFormControl(false),
      absenceType:  new UntypedFormControl(null, Validators.required)
    })

    // update the dateTo if it hasnt been changed to the value of the DateFrom
    this.formGroup.get('dateFrom').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(dateForm => {
        const dateToControl = this.formGroup.get('dateTo');
        if(dateToControl.value?.toDateString() == new Date().toDateString()){
          dateToControl.patchValue(dateForm);
        }
      });
  }

  initFilterForm(){
    this.filterForm = new UntypedFormGroup({
      onlyUnsigned: new UntypedFormControl(false),
      absenceType: new UntypedFormControl(null),
      absenceState: new UntypedFormControl(null),
      from: new UntypedFormControl(new Date(new Date().getFullYear(), 0, 1)),
      to: new UntypedFormControl(new Date(new Date().getFullYear(), 11, 31)),
    })
  }

  onGridReload(){
    this.absenceGroupStore.loadAbsenceGroups();
  }

  openCreate(){
    this.formGroup.reset({
      title: null,
      dateFrom: new Date(),
      isHalfDayStart: false,
      dateTo: new Date(),
      isHalfDayEnd: false,
      absenceType: null,
    });
    this.createOpen = true;
  }

  closeCreate(){
    this.createOpen = false;
  }

  onSubmit(){
    const formValues = this.formGroup.value;

    const request: ICreateAbsenceGroupApplicationRequest = {
      title:  formValues.title,
      dateFrom: new Date(formValues.dateFrom).toDateString(),
      isHalfDayStart: formValues.isHalfDayStart,
      dateTo: new Date(formValues.dateTo).toDateString(),
      isHalfDayEnd:  formValues.isHalfDayEnd,
      absenceType:  formValues.absenceType
    }
    this.absenceGroupStore.applyAbsenceGroup(request);
    this.closeCreate();
  }

  // Hours of the requested overtime compensation that the balance does not cover. Only a hint -
  // saving stays possible.
  flexTimeFallbackHours(employee: IEmployee, tenantSettings: ITenantSettings): number {
    const formValues = this.formGroup?.value;

    if(!tenantSettings?.hasOvertime || formValues?.absenceType !== AbsenceType.OvertimeCompensation){
      return 0;
    }

    return overtimeFlexTimeFallbackHours(
      employee, tenantSettings, formValues.dateFrom, formValues.dateTo,
      formValues.isHalfDayStart, formValues.isHalfDayEnd
    );
  }

  onFilter(){
    const formValues = this.filterForm.value;

    // Rebuild the advanced filter; the employee scope stays on searchAbseceGroupRequest.employeeId.
    delete this.absenceGroupStore.searchAbseceGroupRequest['advancedFilter'];
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter = {}
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.logic = "and";
    this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters = [];

    if(formValues.onlyUnsigned){
      const filter : IFilter = {
        field: 'absenceState',
        operator: 'eq',
        value: '0'
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.absenceType){
      const filter : IFilter = {
        field: 'absenceType',
        operator: 'eq',
        value: formValues.absenceType.toString()
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.absenceState != null){
      const filter : IFilter = {
        field: 'absenceState',
        operator: 'eq',
        value: formValues.absenceState.toString()
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.from){
      const filter : IFilter = {
        field: 'dateFrom',
        operator: 'gte',
        value: formValues.from
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    if(formValues.to){
      const filter : IFilter = {
        field: 'dateTo',
        operator: 'lte',
        value: formValues.to
      }
      this.absenceGroupStore.searchAbseceGroupRequest.advancedFilter.filters.push(filter);
    }

    this.absenceGroupStore.searchAbseceGroupRequest.pageNumber = 1;
    this.onGridReload();
  }

  onResetFilter(){
    this.filterForm.reset();
    this.initFilterForm();
    delete this.absenceGroupStore.searchAbseceGroupRequest['advancedFilter'];
    this.onGridReload();
  }

}
